import { z } from 'zod';
import { optionalMongoIdString } from './mongo-id.schema.js';
import { optionalFinanceCurrencySchema } from './finance-currency.schema.js';

/** Empty strings from forms break Mongoose ObjectId/Date casts — omit them before persist. */
function stripEmptyCrisisEventFields(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
    const out = { ...data };
    const optionalKeys = [
        'code',
        'description',
        'type',
        'severity',
        'status',
        'occurredAt',
        'detectedAt',
        'declaredAt',
        'resolvedAt',
        'impactDescription',
        'rootCause',
        'impactedLocations',
        'incidentCommander',
        'owner',
        'startDate',
        'endDate',
    ];
    for (const key of optionalKeys) {
        if (out[key] === '' || out[key] === null) {
            delete out[key];
        }
    }
    if (out.impactAssessment && typeof out.impactAssessment === 'object') {
        const ia = { ...out.impactAssessment };
        for (const [k, v] of Object.entries(ia)) {
            if (v === '' || v === null) delete ia[k];
        }
        if (Object.keys(ia).length === 0) delete out.impactAssessment;
        else out.impactAssessment = ia;
    }
    return out;
}

const ImpactAssessmentSchema = z
    .object({
        operationalImpact: z.string().nullable().optional(),
        peopleImpact: z.array(z.string()).optional(),
        infrastructureImpact: z.string().nullable().optional(),
        reputationImpact: z.string().nullable().optional(),
        financialLosses: z.coerce.number().nullable().optional(),
        currency: optionalFinanceCurrencySchema,
        expectedDurationValue: z.coerce.number().nullable().optional(),
        expectedDurationUnit: z
            .enum(['minutes', 'hours', 'days', 'weeks', 'months'])
            .nullable()
            .optional(),
    })
    .optional();

const CrisisEventObjectSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    title: z.string().min(1, 'Title is required'),
    code: z.string().optional(),
    description: z.string().optional(),
    type: z.string().optional(),
    severity: z.string().optional(),
    status: z.string().optional(),
    occurredAt: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    detectedAt: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    declaredAt: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    resolvedAt: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    impactDescription: z.string().optional(),
    rootCause: z.string().optional(),
    impactedLocations: z.string().optional(),
    impactAssessment: ImpactAssessmentSchema,
    comments: z
        .array(
            z.object({
                id: z.union([z.string(), z.number()]).optional(),
                content: z.string().optional(),
                author: z.string().optional(),
                date: z.string().optional(),
            }),
        )
        .optional(),
    relatedBias: z.array(z.string()).optional(),
    activatedBcps: z.array(z.string()).optional(),
    affectedProcesses: z.array(z.string()).optional(),
    affectedAssets: z.array(z.string()).optional(),
    affectedVendors: z.array(z.string()).optional(),
    communications: z.array(z.string()).optional(),
    communicationPlans: z.array(z.string()).optional(),
    recoveryResources: z.array(z.string()).optional(),
    recoveryActivities: z.array(z.string()).optional(),
    lessonsLearned: z.array(z.string()).optional(),
    improvementActions: z.array(z.string()).optional(),
    progress: z.coerce.number().optional(),
    owner: optionalMongoIdString,
    startDate: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    endDate: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    incidentCommander: optionalMongoIdString,
    customFields: z.record(z.unknown()).optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
});

export const CrisisEventSchema = CrisisEventObjectSchema;

export const CreateCrisisEventSchema = z.preprocess(
    stripEmptyCrisisEventFields,
    CrisisEventObjectSchema.omit({ id: true, createdAt: true, updatedAt: true }),
);

export const UpdateCrisisEventSchema = z.preprocess(
    stripEmptyCrisisEventFields,
    CrisisEventObjectSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
);

export const CrisisEventFormSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    title: z.string().min(1, 'form.validation.titleRequired'),
    occurredAt: z.union([z.string(), z.coerce.date(), z.date()]).refine((val) => val !== '' && val != null, {
        message: 'form.validation.eventDateRequired',
    }),
    type: z.string().optional(),
    description: z.string().optional(),
    rootCause: z.string().optional(),
    impactedLocations: z.string().optional(),
    status: z.string().optional(),
    resolvedAt: z.union([z.string(), z.coerce.date(), z.date()]).optional(),
    incidentCommander: optionalMongoIdString,
    impactAssessment: z
        .object({
            operationalImpact: z.string().optional(),
            peopleImpact: z.array(z.string()).optional(),
            infrastructureImpact: z.string().optional(),
            reputationImpact: z.string().optional(),
            financialLosses: z.union([z.number(), z.string()]).optional(),
            currency: optionalFinanceCurrencySchema,
            expectedDurationValue: z.union([z.number(), z.string()]).optional(),
            expectedDurationUnit: z.string().optional(),
        })
        .optional(),
    comments: z.array(z.any()).optional(),
});

export const CrisisRecoveryActivityFormSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().min(1, 'form.validation.nameRequired'),
    description: z.string().optional(),
    owner: z.string().optional(),
    team: z.string().optional(),
    startDate: z.union([z.string(), z.date()]).optional(),
    endDate: z.union([z.string(), z.date()]).optional(),
    progress: z.coerce.number().min(0).max(100).optional(),
    externalSupport: z.union([z.boolean(), z.string()]).optional(),
    vendor: z.string().optional(),
});

export const CrisisImprovementActionFormSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    code: z.string().optional(),
    name: z.string().min(1, 'form.validation.nameRequired'),
    description: z.string().optional(),
    owner: z.string().optional(),
    budget: z.coerce.number().min(0).optional(),
    currency: optionalFinanceCurrencySchema,
    startDate: z.union([z.string(), z.date()]).optional(),
    endDate: z.union([z.string(), z.date()]).optional(),
    progress: z.coerce.number().min(0).max(100).optional(),
    status: z.string().optional(),
    exercise: z.string().optional(),
});
